import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, DropResult, ResponderProvided } from "react-beautiful-dnd";
import { Link, useLocation } from "react-router-dom";
import { isEqual } from "lodash-es";

import { slideInFromTop } from "shared/styles/constants";
import { useBreakpoint } from "shared/utils";
import { copy } from "shared/assets";

import { SideMenuContainer, MenuHeader, MenuContent, StyledSideMenuProps } from "./styles";
import { MainMenuItem, MenuToggle, AddMenu } from "./components";
import { ISideMenuItem, IMenuItemType } from "./types";
import { isActiveItem } from "./utils";
import { useEventContext } from "Bands/contexts";

export type ISideMenuType = "venue" | "stage" | "event";

interface SideMenuProps extends StyledSideMenuProps {
  type?: ISideMenuType;
  title: string;
  titleUrl?: string;
  showAddMenu?: boolean;
  addItems?: ISideMenuItem[]; // + menu items
  items?: ISideMenuItem[];
  canDragItems?: boolean;
  dragTooltipCopy?: string;
  disableUpdateList?: boolean;
  onDragEnd?: (newList: ISideMenuItem[]) => void;
}

const reorder = (list: ISideMenuItem[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const SideMenu: React.FC<SideMenuProps> = ({
  type = "venue",
  title,
  titleUrl,
  showAddMenu,
  addItems,
  items,
  canDragItems,
  dragTooltipCopy,
  disableUpdateList,
  onDragEnd,
  accentColor,
}) => {
  const location = useLocation();
  const { fetchingEventBlocks } = useEventContext();

  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [list, setList] = useState(items);
  const { pathname } = useLocation();
  const isMobile = useBreakpoint();
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // update list
  useEffect(() => {
    if (!disableUpdateList && !isEqual(items, list)) {
      setList(items);
    }
  }, [items, list, disableUpdateList]);

  const isDragEnabled = useMemo(() => {
    if (!canDragItems) return false;

    const draggableItems = list?.filter(({ type }) => type === IMenuItemType.draggable) || [];

    // if there are 1 or less draggable items drag is disabled
    if (draggableItems.length <= 1) return false;

    // check if any of the default items are active
    const hasActiveDraggableItems =
      draggableItems.filter(
        ({ path, matchPaths }) => pathname === path || isActiveItem({ path, matchPaths, location })
      ).length !== 0;
    return !hasActiveDraggableItems;
  }, [canDragItems, list, pathname, location]);

  const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
    // dropped outside the list
    if (!result.destination || !list) {
      return;
    }

    const newList = reorder(list, result.source.index, result.destination.index);

    onDragEnd && onDragEnd(newList);

    setList(newList);
  };

  const closeAllMenus = () => {
    setIsAddMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const renderMenuHeader = useCallback(() => {
    if (isMobile) {
      return (
        <MenuHeader
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          accentColor={accentColor}
          isMobile={isMobile}
        >
          <span>{title}</span>
          <MenuToggle isOpen={isMobileMenuOpen} />
        </MenuHeader>
      );
    }

    if (titleUrl) {
      return (
        <MenuHeader as={Link} to={titleUrl} accentColor={accentColor} isMobile={isMobile}>
          <span>{title}</span>
        </MenuHeader>
      );
    }

    return (
      <MenuHeader accentColor={accentColor} isMobile={isMobile}>
        <span>{title}</span>
      </MenuHeader>
    );
  }, [isMobile, titleUrl, accentColor, isMobileMenuOpen, title]);

  const canShowAddMenu = showAddMenu && addItems?.length !== 0 && !fetchingEventBlocks;

  return (
    <SideMenuContainer {...slideInFromTop} aria-label={copy.a11y.sideNav}>
      {renderMenuHeader()}
      {canShowAddMenu && (
        <AddMenu
          isOpen={isMobileMenuOpen}
          isMenuOpen={isAddMenuOpen}
          type={type}
          items={addItems}
          onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
          closeMobileMenu={() => setIsMobileMenuOpen(false)}
          accentColor={accentColor}
        />
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sideMenu">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <MenuContent
                isOpen={isMobileMenuOpen}
                {...slideInFromTop}
                data-testid="side_menu_items"
              >
                {list?.map((item, index) => (
                  <MainMenuItem
                    key={item.id}
                    item={item}
                    index={index}
                    dragEnabled={isDragEnabled}
                    dragTooltipCopy={dragTooltipCopy}
                    handleClose={closeAllMenus}
                    accentColor={accentColor}
                    expandedItemId={expandedItemId}
                    setExpandedItemId={setExpandedItemId}
                  />
                ))}
              </MenuContent>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </SideMenuContainer>
  );
};
